import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import useMeasure from 'react-use-measure';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import LoadingOverlay from 'components/LoadingOverlay';
import { H1 } from 'components/Typography';
import { getEntriesByContentTypeAndProduct } from 'api/contentful';
import { SearchProvider } from './hooks/useSearch';
import SearchInputHeader from './components/SearchInputHeader';
import Questions from './components/Questions';
import { POLICY_COLOR_THEMES } from './policyColors';
import {
  GAP,
  Wrapper,
  Sticky,
  StickyHeader,
  Header,
  Content,
  HeaderWave,
  QuestionsContent,
  TitleHeader,
  StyledSideNav,
  StyledDropdownNav,
  DownloadLink
} from './styled';

const Policy = ({
  product = 'nomad-insurance',
  title = 'Nomad Insurance Essential description of coverage',
  color = 'picton',
  hideAddonLabel = false
}) => {
  const [questions, setQuestions] = useState([]);
  const [pdfUrl, setPdfUrl] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const urlVersion = useMemo(() => queryString.parse(location.search).version, []);
  const [headerMeasureRef, headerBounds] = useMeasure();

  const fetchData = async () => {
    const data = await getEntriesByContentTypeAndProduct('policy', product, 5);
    let selectedVersion = data.sort(
      (a, b) => new Date(b.fields.effectiveDate) - new Date(a.fields.effectiveDate)
    )[0];
    if (urlVersion) {
      const versionInUrl = data.find(
        (policy) => policy.fields.effectiveDate === urlVersion
      );
      selectedVersion = versionInUrl || data[0];
    }
    setQuestions(selectedVersion.fields.questions);
    setPdfUrl(selectedVersion.fields.policyDocumentPdf?.fields.file?.url);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [urlVersion]);

  const headerHeight = headerBounds.height + GAP;

  return (
    <Wrapper style={{ '--header-height-measure': headerHeight }}>
      <SearchProvider>
        <StickyHeader ref={headerMeasureRef}>
          <Header $color={color}>
            <Content>
              <TitleHeader>
                <H1>{title}</H1>
                <DownloadLink href={pdfUrl} target="_blank" rel="noopener noreferrer">
                  View PDF
                </DownloadLink>
              </TitleHeader>
              <SearchInputHeader />
            </Content>
          </Header>
          <HeaderWave $color={color} />
          {!isLoading && (
            <Content>
              <StyledDropdownNav
                questions={questions}
                scrollOffset={headerHeight}
                color={color}
              />
            </Content>
          )}
        </StickyHeader>

        {isLoading ? (
          <LoadingOverlay isLoading variant="light" style={{ zIndex: 1 }} />
        ) : (
          <Content>
            <QuestionsContent>
              <Sticky>
                <StyledSideNav
                  questions={questions}
                  scrollOffset={headerHeight}
                  color={color}
                />
              </Sticky>
              <Questions questions={questions} hideAddonLabel={hideAddonLabel} />
            </QuestionsContent>
          </Content>
        )}
      </SearchProvider>
    </Wrapper>
  );
};

Policy.propTypes = {
  product: PropTypes.string,
  title: PropTypes.string,
  color: PropTypes.oneOf(Object.keys(POLICY_COLOR_THEMES)),
  hideAddonLabel: PropTypes.bool
};

export default Policy;
